/**
 * Makes a card's ⋯ (actions) control reachable to a SCREEN READER when the card
 * itself is one accessibility element.
 *
 * THE DEFECT THIS EXISTS FOR. A DS card's container `Pressable` sets
 * `accessibilityRole: 'button'` and an `accessibilityLabel` so the whole card is
 * one activatable item ("open this deck"). On iOS that makes the container THE
 * accessibility element and HIDES every descendant — so the ⋯ inside it is not
 * an accessibility node at all, even though it carries its own
 * `accessibilityLabel`. Verified on device (2026-09-04): a scoped snapshot of a
 * Home/Library deck card returns exactly ONE node, the card, with no children.
 * No VoiceOver user could open a deck's options menu from any card.
 *
 * WHY NOT `accessible={false}` ON THE CONTAINER. Un-merging is the fix this repo
 * already uses for bottom sheets and the study strip, and it is the wrong one
 * here: the card's label and `button` role live ON the container, so making it
 * non-accessible surfaces the ⋯ at the cost of the card's PRIMARY action — "open
 * this deck" stops being a single activatable item and becomes a handful of
 * unactionable Texts. Trading the main action for the secondary one is not an
 * accessibility fix.
 *
 * A custom action keeps the card one item, reads its title once, and offers the
 * ⋯ in VoiceOver's Actions rotor — which is what iOS does natively for a row
 * with a secondary control. Declaring it does not disturb default activation: a
 * double-tap still runs the card's own `onPress`.
 *
 * NATIVE ONLY, BY CONSTRUCTION. The result is meant for a card's `nativeProps`,
 * which the WEB `Pressable` discards outright (see its `nativeProps:
 * _nativeProps`). Web needs nothing: those containers render `as="div"` with
 * `role="button"` precisely so the ⋯ can be a real nested `<button>`, so both
 * are already separately reachable there. The asymmetry is the two platforms'
 * accessibility models differing — not a fork.
 */

/** The action name both the card and its tests refer to, so neither hardcodes it. */
export const CARD_ACTIONS_A11Y_ACTION = 'options';

export type CardActionsA11yProps = {
  accessibilityActions: { name: string; label: string }[];
  onAccessibilityAction: (event: { nativeEvent: { actionName: string } }) => void;
};

/**
 * Returns the native accessibility props exposing `onActionsPress` as a custom
 * action, or `null` when there is nothing to expose.
 *
 * `null` when the card has no actions handler, when it is disabled, or when
 * `actionsLabel` is missing — a custom action with no label is announced as
 * nothing, which is worse than not offering it, and the DS carries no i18n so
 * this cannot invent copy. `actionsLabel` is therefore load-bearing rather than
 * decorative wherever `onActionsPress` is passed.
 */
export function cardActionsA11yProps(input: {
  onActionsPress?: (event?: never) => void;
  actionsLabel?: string;
  disabled?: boolean;
}): CardActionsA11yProps | null {
  const { onActionsPress, actionsLabel, disabled } = input;
  if (!onActionsPress || disabled || !actionsLabel) return null;
  return {
    accessibilityActions: [{ name: CARD_ACTIONS_A11Y_ACTION, label: actionsLabel }],
    onAccessibilityAction: (event) => {
      if (event.nativeEvent.actionName === CARD_ACTIONS_A11Y_ACTION) {
        onActionsPress();
      }
    },
  };
}
